// SignupPage.js
// Shows email/password signup form and creates user in Firestore

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';

function SignupPage() {
  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Error and loading states
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  // This function runs when user submits the form
  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    // Simple validation
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      setLoading(false);
      return;
    }

    try {
      // Step 1: Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );

      // Get the new user's ID
      const userId = userCredential.user.uid;

      // Step 2: Create user profile in Firestore
      // This stores additional info like email and family ID
      // firstName will be set during onboarding
      await setDoc(doc(db, 'users', userId), {
        email: email,
        familyId: null, // Will be set when they create/join a family
        createdAt: serverTimestamp()
      });

      // Redirect to home (App.js will send them to family setup)
      navigate('/');
    } catch (err) {
      console.error('Signup error:', err);

      // Show user-friendly error messages
      if (err.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Use at least 6 characters.');
      } else {
        setError('Failed to create account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <h1>Create your account</h1>
      <p>Get started with Kana</p>

      <form onSubmit={handleSubmit} className="auth-form">
        {/* Email input */}
        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
          />
        </div>

        {/* Password input */}
        <div className="form-group">
          <label htmlFor="password">Password</label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 6 characters"
            required
          />
        </div>

        {/* Show error if there is one */}
        {error && <p className="form-error">{error}</p>}

        {/* Submit button */}
        <button
          type="submit"
          className="button button-primary"
          disabled={loading}
        >
          {loading ? 'Creating account...' : 'Create account'}
        </button>
      </form>

      {/* Link to login page */}
      <div className="auth-link">
        <p>
          Already have an account? <Link to="/login">Log in</Link>
        </p>
      </div>
    </div>
  );
}

export default SignupPage;
